// src/PublicFormPage.jsx — Phase 2.E.12 (ADR-045)
// Page publique de formulaire, accessible sans authentification via
// l'URL /forms/{token}. Routée depuis index.js par window.location.pathname.
//
// LOT E V4 — ce fichier est désormais le CONTENEUR DATA (token →
// /api/form-config → submitFormToken → upload pièces). Le RENDU est délégué au
// composant présentationnel partagé `GuestFormView` (src/components/GuestFormView.jsx),
// réutilisé tel quel par l'aperçu admin (FormulairesTab). Le chemin voyageur
// (data + soumission + upload) est inchangé.

import { useEffect, useState } from "react"
import { submitFormToken } from "./lib/forms-api"
import { migrateLegacyToUnified } from "./lib/forms-migration"
import { uploadIdDocument, removeIdDocument, FIELD_REF_TO_DOC_REF } from "./lib/id-upload"
import { DSV as DS } from "./lib/charte-voyageur"
import {
  t, DEFAULT_FORM_LOCALE,
  normalizeFormLocale, staticFieldLabel, staticContentTr,
} from "./lib/form-i18n"
import GuestFormView, {
  shouldRenderField, substituteVars,
  Centered, titleStyle, msgStyle, GuestFormRecu,
  champsEnModification, valeursModification,
} from "./components/GuestFormView"
import GuestFormViewV2, {
  GuestFormMerciV2, GuestFormExpireV2,
} from "./components/GuestFormViewV2"

// ═══ LA CARTE DU DRAPEAU (B2 — pièce 4 du plan V2) ═════════════════════════
// `form_v2` arrive DANS la charge /api/form-config (précédent exact
// ESPACE_SEJOUR_V2_ENABLED : le front ne lit aucune env). Branche par branche :
//
// | Comportement            | Drapeau ÉTEINT (V1, prod)     | Drapeau ALLUMÉ (V2)         |
// |-------------------------|-------------------------------|-----------------------------|
// | Écran expiré (410)      | step:"error" générique intact | écran dédié maquette        |
// | Écran merci             | redirection auto 1,5 s intacte| « Nous avons tout reçu »    |
// |                         |                               | + CTA si journey_url        |
// | Validation à l'envoi    | message global unique intact  | carte d'erreurs par ref     |
// | Persistance             | aucune                        | sessionStorage par token,   |
// |                         |                               | effacé à la soumission (C5) |
// | Ensemencement uploads   | aucun (trou 409-reload CONNU  | documents → uploads (B1)    |
// |                         | de la V1, corrigé à la bascule)|                            |
// | Rendu                   | GuestFormView                 | GuestFormViewV2             |
//
// TOUT ce qui suit derrière `formV2` est nouveau ; drapeau éteint, chaque
// chemin V1 est SYNTAXIQUEMENT inchangé (suite Jest existante verte sans
// modification = la preuve).

// Ensemencement B1 : documents serveur [{ref,status,controle}] → état uploads.
// Reverse du mapping client fieldRef→docRef. Tous les statuts présents côté
// serveur donnent une ligne verte (fail-open : l'ambre INVITE, ne bloque pas ;
// `verified` restauré = le verrou 409-au-reload ne peut plus bloquer l'envoi).
const DOC_REF_TO_FIELD_REF = Object.fromEntries(
  Object.entries(FIELD_REF_TO_DOC_REF).map(([f, d]) => [d, f]),
)
function seedUploadsFromDocuments(documents) {
  const seeded = {}
  for (const d of Array.isArray(documents) ? documents : []) {
    const fieldRef = DOC_REF_TO_FIELD_REF[d.ref]
    if (!fieldRef) continue
    seeded[fieldRef] = { status: 'done', restored: true, controle: d.controle || null }
  }
  return seeded
}

// Persistance C5 (verdict Q-PERSISTANCE) : sessionStorage par token — survit
// au RECHARGEMENT, PAS à la fermeture de l'onglet ; effacé à la soumission
// réussie. On ne promet rien de plus à l'écran (v2_sub_persistance).
const storageKey = (token) => `fbz-form-v2:${token}`
function loadStoredValues(token) {
  try {
    const raw = window.sessionStorage.getItem(storageKey(token))
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch (_) { return null }
}
function storeValues(token, values) {
  try { window.sessionStorage.setItem(storageKey(token), JSON.stringify(values)) } catch (_) {}
}
function clearStoredValues(token) {
  try { window.sessionStorage.removeItem(storageKey(token)) } catch (_) {}
}

// LOT B5 — langue initiale depuis l'URL (?lang=) sinon défaut FR. Pas de
// persistance DB (décision Dimitri) : l'état vit dans l'URL + le state React.
function initialLang() {
  try {
    const q = new URLSearchParams(window.location.search).get('lang')
    return normalizeFormLocale(q)
  } catch (_) {
    return DEFAULT_FORM_LOCALE
  }
}

export default function PublicFormPage({ token }) {
  const [step, setStep] = useState("loading") // loading | error | form | submitting | done | expired (V2) | received
  // Formulaire DÉJÀ ENVOYÉ (défaut voyageur 01/10) : { submitted_at, reponses,
  // piece_recue } servi par /api/form-config → écran en lecture seule.
  const [dejaRecu, setDejaRecu] = useState(null)
  // Relecture de la configuration (après un 409 'deja_recu' à l'envoi).
  const [reloadKey, setReloadKey] = useState(0)
  // Mode MODIFICATION (décision Dimitri 03/10) : le voyageur a cliqué
  // « Modifier mes réponses » sur l'écran « Bien reçu » (fiche encore
  // modifiable d'après le serveur). Survit au changement de langue.
  const [editing, setEditing] = useState(false)
  // Limite de modification servie par /api/form-config (relectures du 28/10) :
  // { jusqu_au, avec_heure } | null. Seule source des phrases « Vous pourrez
  // modifier vos réponses jusqu'au … » ; null = aucune promesse.
  const [limiteModification, setLimiteModification] = useState(null)
  // 409 'deja_recu' à l'envoi : l'écran « Bien reçu » dit que CET envoi n'a
  // PAS été enregistré (jamais de fausse confirmation, noyau 10).
  //   'trop_tard'     — MODIFICATION refusée (fiche figée entre-temps) ;
  //   'autre_reponse' — PREMIER envoi refusé : une réponse avait déjà été
  //                     reçue pour ce séjour (par un autre lien).
  const [avisRefus, setAvisRefus] = useState(null)
  const [error, setError] = useState(null)
  // ── État V2 (inerte drapeau éteint) ──────────────────────────────────────
  const [formV2, setFormV2] = useState(false)
  const [guestFirstName, setGuestFirstName] = useState(null)
  const [guestDisplayName, setGuestDisplayName] = useState(null)
  const [fieldErrors, setFieldErrors] = useState(null) // B2 : carte d'erreurs par reference
  const [rgpdOpen, setRgpdOpen] = useState(false)
  // tokenInfo loaded but unused after substituting vars — kept for future
  // (e.g., display reservation context to the visitor).
  const [, setTokenInfo] = useState(null)
  const [formConfig, setFormConfig] = useState(null) // { title, description, fields, cover_photo_url, cover_photo_b64, redirect_url }
  const [logementName, setLogementName] = useState("")
  const [schedule, setSchedule] = useState({ checkin_start: '', checkin_end: '', checkout_time: '' })
  const [reservation, setReservation] = useState(null) // { start_date, end_date } pré-affichage read-only ADR-072
  const [journeyUrl, setJourneyUrl] = useState(null) // /sejour/<token> si résa directe (point 3)
  // ADR-189 amendement — pièce d'identité exigée pour ce (logement, canal).
  // Défaut true (rétrocompat si le champ est absent de la réponse form-config).
  const [idDocRequired, setIdDocRequired] = useState(true)
  // Décision produit 2026-09-06 — adresse de facturation retirée pour Airbnb.
  // Même fail-safe : défaut true, seule la clé explicitement false masque.
  const [billingAddressRequired, setBillingAddressRequired] = useState(true)
  const [values, setValues] = useState({})
  // N2-IA LOT 1 (ADR-189) — état d'upload par champ pièce d'identité.
  // Forme : { [fieldRef]: { status:'idle'|'uploading'|'done'|'failed', name, error } }
  const [uploads, setUploads] = useState({})
  // LOT B5 — langue active du formulaire voyageur (FR/EN/ES). Init URL ?lang=.
  const [lang, setLang] = useState(initialLang)

  // Lance l'upload asynchrone (non bloquant) d'une pièce d'identité dès la
  // sélection du fichier. Le submit du formulaire vérifiera l'état 'done'.
  async function startIdUpload(fieldRef, file) {
    if (!file) {
      setUploads(prev => { const n = { ...prev }; delete n[fieldRef]; return n })
      return
    }
    setUploads(prev => ({ ...prev, [fieldRef]: { status: 'uploading', name: file.name } }))
    try {
      // C4 (V2) : le confirm affiche « Vérification de votre document… »
      // (phase 'checking') — V1 garde son unique « Envoi en cours… ».
      const out = await uploadIdDocument({
        token, fieldRef, file,
        onPhase: formV2
          ? (phase) => setUploads(prev => ({ ...prev, [fieldRef]: { ...(prev[fieldRef] || {}), status: phase } }))
          : undefined,
      })
      // E1a (contradicteur post-montage) — chemin 409-succès (already_resolved
      // + verified) : le serveur a GARDÉ l'ANCIEN document, le fichier
      // fraîchement choisi n'est jamais parti. Afficher son nom ferait mentir
      // la ligne verte → aucun nom sur ce chemin (V1 : libellé générique
      // « Document déjà reçu » via `restored` ; V2 : ligne type·nom-résa,
      // déjà sans nom de fichier).
      const alreadyResolved = !!(out && out.alreadyResolved)
      setUploads(prev => ({
        ...prev,
        [fieldRef]: formV2
          ? { status: 'done', ...(alreadyResolved ? {} : { name: file.name }), controle: (out && out.controle) || null, restored: alreadyResolved }
          : alreadyResolved
            ? { status: 'done', restored: true }
            : { status: 'done', name: file.name },
      }))
    } catch (e) {
      // Aucune PII dans l'erreur affichée — message générique i18n + code.
      const msg = e.code === 'unsupported_format' || e.code === 'decode_failed'
        ? t(lang, 'upload_err_format')
        : t(lang, 'upload_err_generic')
      setUploads(prev => ({ ...prev, [fieldRef]: { status: 'failed', name: file.name, error: msg } }))
    }
  }

  // LOT B5 — changement de langue : met à jour l'URL (?lang=) sans recharger,
  // l'état déclenche le refetch (form-config?lang=) via la dépendance useEffect.
  function changeLang(next) {
    const loc = normalizeFormLocale(next)
    setLang(loc)
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('lang', loc)
      window.history.replaceState(null, '', url)
    } catch (_) {}
  }

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        // Lot B (décision A) : projection minimale via endpoint public dédié —
        // on ne lit PLUS le blob cfg entier (548 KB incl iban/bic/siret/claudeKey/
        // codes) côté client anon. /api/form-config résout le token + renvoie
        // uniquement les infos du logement ciblé + schedule + dates résa.
        const res = await fetch(`/api/form-config?token=${encodeURIComponent(token)}`)
        const fc = await res.json().catch(() => ({}))
        // B2 — écran expiré DÉDIÉ (drapeau V2 seulement) : le 410 porte
        // form_v2. Drapeau éteint → chemin actuel intact (step:"error").
        if (res.status === 410 && fc && fc.form_v2 === true) {
          if (mounted) { setFormV2(true); setStep("expired") }
          return
        }
        if (!res.ok) throw new Error(fc.error || "Configuration FBZ introuvable")
        if (!mounted) return
        // ── V2 (champs additifs — inertes drapeau éteint) ────────────────
        const v2 = fc.form_v2 === true
        setFormV2(v2)
        if (v2) {
          setGuestFirstName(fc.guest_first_name || null)
          const dn = [fc.guest_first_name, fc.guest_last_name].filter(Boolean).join(' ')
          setGuestDisplayName(dn || null)
          // B1 — ensemencement des uploads depuis l'état serveur : la ligne
          // verte se restaure d'elle-même après un rechargement.
          const seeded = seedUploadsFromDocuments(fc.documents)
          if (Object.keys(seeded).length) setUploads((prev) => ({ ...seeded, ...prev }))
          // C5 — restauration des réponses (sessionStorage par token).
          const stored = loadStoredValues(token)
          if (stored) setValues((prev) => ({ ...stored, ...prev }))
        }
        const tk = { type: fc.type, logement_id: fc.logement?.id, reservation_id: fc.reservation_id }
        setTokenInfo(tk)
        const log = fc.logement
        if (!log) throw new Error("Logement introuvable")
        setLogementName(log.nom_complet || log.nom || log.id)
        const effectiveSchedule = fc.schedule || { checkin_start: '', checkin_end: '', checkout_time: '' }
        setSchedule(effectiveSchedule)
        const rsv = fc.reservation || null
        if (rsv && mounted) setReservation(rsv)
        if (fc.journey_url && mounted) setJourneyUrl(fc.journey_url)
        if (mounted) {
          setLimiteModification(fc.limite_modification && typeof fc.limite_modification === 'object' ? fc.limite_modification : null)
        }
        // ADR-189 amendement — masquage du module pièce si non requis pour ce
        // canal/logement. Seul `=== false` désactive (absent → reste requis).
        if (mounted) setIdDocRequired(fc.id_doc_required !== false)
        // Décision produit 2026-09-06 — masquage du champ adresse de facturation
        // si non demandé pour ce canal (Airbnb par défaut). Seul `=== false`
        // désactive (clé absente de la réponse → le champ reste).
        if (mounted) setBillingAddressRequired(fc.billing_address_required !== false)
        // ADR-074 — la migration lazy était précédemment uniquement appliquée
        // dans FormulairesTab admin. Conséquence : si l'admin générait un lien
        // unified sans avoir cliqué Enregistrer, log.forms.unified n'existait
        // pas en cfg → page publique levait "Ce formulaire n'est pas encore
        // configuré côté FBZ".
        //
        // Fix : on applique migrateLegacyToUnified(log.forms) côté voyageur
        // aussi, pour reconstruire le format unified à la volée depuis les
        // welcome+checkin legacy (ou depuis le default si rien). Idempotent,
        // pas de save côté serveur — la cfg en base reste inchangée jusqu'au
        // premier édit admin.
        //
        // Pour les anciens tokens 'welcome'/'checkin' (rétrocompat lecture),
        // on tente d'abord le format direct, et si absent on retombe sur le
        // format unifié migré.
        // Décision produit 2026-06-12 : on annule la transformation « pièce
        // unique » — le formulaire restaure le choix explicite passeport / CNI
        // (radio id_type + uploads conditionnels) tel que produit par
        // migrateLegacyToUnified, sans collapse.
        let config = null
        if (tk.type === 'unified') {
          config = migrateLegacyToUnified(log.forms)
        } else if (log.forms?.[tk.type] && Array.isArray(log.forms[tk.type].fields)) {
          config = log.forms[tk.type]
        } else {
          config = migrateLegacyToUnified(log.forms)
        }
        if (!config) throw new Error("Ce formulaire n'est pas encore configuré côté FBZ.")

        // Rétrocompat : flatten sections legacy en fields plats si besoin
        let fields = config.fields
        if (!Array.isArray(fields) && Array.isArray(config.sections)) {
          fields = config.sections.flatMap(s => s.fields || [])
        }
        if (!Array.isArray(fields) || fields.length === 0) {
          throw new Error("Ce formulaire n'est pas encore configuré côté FBZ.")
        }
        // LOT B5 — traduction STATIC-FIRST (décision Dimitri) :
        //  1. Les libellés STANDARD (champs par défaut) + titre/description par
        //     défaut sont traduits par DICTIONNAIRE (staticFieldLabel /
        //     staticContentTr) — fiable, instantané, gratuit, zéro IA.
        //  2. Seuls les libellés/textes CUSTOM (≠ défaut) partent à l'IA via
        //     /api/form-translate, qui applique un garde-fou anti-bavardage
        //     (méta-réponse rejetée → fallback FR, jamais de phrase parasite).
        // En FR ou en cas d'échec → tout reste FR (jamais bloquant).
        let dyn = {}
        if (lang !== 'fr') {
          // a) résolution statique d'abord.
          const aiSet = new Set()
          const addAi = (s) => { if (typeof s === 'string' && s.trim()) aiSet.add(s) }
          const stTitle = staticContentTr(lang, config.title)
          if (config.title) { if (stTitle) dyn[config.title] = stTitle; else addAi(config.title) }
          const stDesc = staticContentTr(lang, config.description)
          if (config.description) { if (stDesc) dyn[config.description] = stDesc; else addAi(config.description) }
          for (const f of fields) {
            if (!f || !f.label) continue
            const st = staticFieldLabel(lang, f.reference, f.label)
            if (st) dyn[f.label] = st
            else addAi(f.label)
          }
          // b) IA uniquement pour le reste (custom), sous garde-fou serveur.
          const toTranslate = [...aiSet]
          if (toTranslate.length) {
            try {
              const tres = await fetch('/api/form-translate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lang, strings: toTranslate }),
              })
              const tjson = await tres.json().catch(() => ({}))
              if (tjson && tjson.dict) dyn = { ...dyn, ...tjson.dict }
            } catch (_) {
              // best-effort : on garde le FR si la traduction échoue.
            }
          }
        }
        if (!mounted) return
        // Lookup tolérant au trim : le serveur keye le dict par chaîne trimmée
        // (validation longueur) — on tente la clé brute puis trimmée.
        const tr = (s) => (s && (dyn[s] || dyn[s.trim()])) || s
        const i18nFields = fields.map((f) => (f && f.label ? { ...f, label: tr(f.label) } : f))
        // Variables dispo dans templates : {logement_name}, {logement_full_name},
        // {checkin_start}, {checkin_end}, {checkout_time}, {arrival_date},
        // {departure_date} (les 2 derniers vides si pas de résa associée).
        setFormConfig({
          title: substituteVars(tr(config.title), log, effectiveSchedule, rsv),
          description: substituteVars(tr(config.description), log, effectiveSchedule, rsv),
          redirect_url: config.redirect_url || null,
          // Les deux clés voyagent ; la vue tranche via resolveCoverPhoto
          // (adresse d'abord, base64 en compatibilité).
          cover_photo_url: config.cover_photo_url || null,
          cover_photo_b64: config.cover_photo_b64 || null,
          fields: i18nFields,
        })
        // Déjà envoyé pour cette réservation : lecture seule, jamais un
        // formulaire vide renvoyable. La saisie gardée dans le navigateur
        // n'a plus d'objet.
        if (fc.deja_recu && typeof fc.deja_recu === 'object') {
          setDejaRecu(fc.deja_recu)
          clearStoredValues(token)
          // Changement de langue PENDANT une modification : on reste dans le
          // formulaire (saisie conservée), libellés retraduits.
          if (editing && fc.deja_recu.modifiable === true) {
            setFormConfig((prev) => ({ ...prev, fields: champsEnModification(i18nFields, fc.deja_recu, lang) }))
            setStep("form")
            return
          }
          setEditing(false)
          setStep("received")
          return
        }
        setStep("form")
      } catch (e) {
        if (!mounted) return
        setError(e.message || String(e))
        setStep("error")
      }
    })()
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, lang, reloadKey])

  // Échec d'envoi : « déjà reçu » → relecture de la configuration, qui
  // bascule sur l'écran en lecture seule ; « en cours » / « vérification
  // impossible » → message traduit, la saisie reste à l'écran.
  function handleSubmitError(e2) {
    if (e2 && e2.code === 'deja_recu') {
      // Fiche figée entre-temps (arrivée passée, onglet resté ouvert) :
      // retour à l'écran « Bien reçu » en lecture seule, qui dit que cet
      // envoi n'a pas été enregistré (modification ou premier envoi).
      setAvisRefus(editing ? 'trop_tard' : 'autre_reponse')
      setEditing(false)
      clearStoredValues(token)
      setStep("loading")
      setReloadKey((k) => k + 1)
      return
    }
    if (e2 && e2.code === 'envoi_en_cours') setError(t(lang, 'recu_envoi_en_cours'))
    else if (e2 && e2.code === 'verification_impossible') setError(t(lang, 'recu_verif_impossible'))
    else setError(e2.message || String(e2))
    setStep("form")
  }

  // « Modifier mes réponses » : le MÊME formulaire, pré-rempli de ce que le
  // serveur renvoie en clair (heures, choix) ; le reste déjà reçu devient
  // facultatif (« laissez vide pour garder »). La pièce déjà reçue n'est pas
  // redemandée. L'envoi met à jour la même fiche (jamais une deuxième).
  function startModification() {
    if (!formConfig || !dejaRecu) return
    setValues(valeursModification(formConfig.fields, dejaRecu))
    setFormConfig((prev) => ({ ...prev, fields: champsEnModification(prev.fields, dejaRecu, lang) }))
    setError(null)
    setFieldErrors(null)
    setAvisRefus(null)
    setEditing(true)
    setStep("form")
  }
  // Pièce d'identité déjà reçue + modification : le module pièce disparaît
  // (rendu ET validation), comme quand la pièce n'est pas demandée.
  const idDocEffectif = editing && dejaRecu && dejaRecu.piece_recue ? false : idDocRequired

  function setValue(refKey, v) {
    setValues(prev => {
      const next = { ...prev, [refKey]: v }
      // C5 (V2 seulement) : persistance sessionStorage par token à chaque
      // frappe — survit au rechargement, effacée à la soumission réussie.
      if (formV2) storeValues(token, next)
      return next
    })
    // B2 (V2) : corriger un champ efface son erreur sous champ.
    if (formV2) {
      setFieldErrors(prev => {
        if (!prev || !prev[refKey]) return prev
        const next = { ...prev }
        delete next[refKey]
        return Object.keys(next).length ? next : null
      })
    }
  }

  // ✕ — retrait RÉEL d'un document (M3, V2 seulement — le composant V1 n'a
  // pas de ✕). Q-CROIX-VERIFIED : autorisé aussi sur un document vérifié tant
  // que le formulaire n'est pas soumis (le serveur fait foi).
  async function handleRemoveIdFile(fieldRef) {
    const prevState = uploads[fieldRef]
    // Optimiste : la ligne disparaît tout de suite ; restaurée si échec serveur.
    setUploads(prev => { const n = { ...prev }; delete n[fieldRef]; return n })
    try {
      await removeIdDocument({ token, fieldRef })
    } catch (e) {
      if (e && e.code === 'not_found') return // rien côté serveur : état déjà propre
      setUploads(prev => ({ ...prev, [fieldRef]: prevState }))
    }
  }

  // B2 (V2) — validation à la tentative d'envoi → CARTE D'ERREURS PAR
  // REFERENCE (rendue sous champ DANS GuestFormViewV2, défilement au premier
  // fautif). Plus de bandeau global en tête — sauf erreur SERVEUR de
  // soumission (avis rouge). Drapeau éteint : handleSubmit V1 intact.
  async function handleSubmitV2(e) {
    e.preventDefault()
    setError(null)
    const errs = {}
    for (const f of formConfig.fields) {
      if (!shouldRenderField(f, values, idDocEffectif, billingAddressRequired)) continue
      if (f.type === 'file') {
        const u = uploads[f.reference]
        if (f.required && (!u || (u.status !== 'done'))) {
          errs[f.reference] = (u && (u.status === 'uploading' || u.status === 'checking'))
            ? t(lang, 'v2_err_piece_en_cours')
            : t(lang, 'v2_err_piece')
        }
        continue
      }
      const v = values[f.reference]
      const vide = v == null || v === '' || (Array.isArray(v) && v.length === 0)
      if (f.required && vide) {
        errs[f.reference] = f.type === 'time' ? t(lang, 'v2_err_horaire') : t(lang, 'v2_err_champ')
        continue
      }
      // Format email (maquette : « Cette adresse ne semble pas valide — … »).
      if (!vide && f.input_type === 'email' && typeof v === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) {
        errs[f.reference] = t(lang, 'v2_err_email')
      }
    }
    if (Object.keys(errs).length) {
      setFieldErrors(errs)
      return
    }
    setFieldErrors(null)
    setStep('submitting')
    try {
      await submitFormToken(token, values)
      // C5 : la persistance s'efface à la soumission réussie.
      clearStoredValues(token)
      setStep('done')
    } catch (e2) {
      handleSubmitError(e2)
    }
  }

  async function handleSubmit(e) {
    if (formV2) return handleSubmitV2(e)
    e.preventDefault()
    setError(null)
    // Validation : champs required, en sautant les champs cachés par le
    // choix de pièce d'identité (ADR-071 conditionnel).
    for (const f of formConfig.fields) {
      if (!shouldRenderField(f, values, idDocEffectif, billingAddressRequired)) continue
      // N2-IA LOT 1 — les champs pièce d'identité (type 'file') sont validés sur
      // l'état d'upload confirmé ('done'), pas sur values. Le submit est bloqué
      // tant que les uploads obligatoires (passeport=1 OU recto+verso=2 selon
      // id_type) ne sont pas confirmés serveur.
      if (f.type === 'file') {
        const u = uploads[f.reference]
        if (f.required && (!u || u.status !== 'done')) {
          if (u && u.status === 'uploading') {
            setError(t(lang, 'upload_in_progress', { x: f.label }))
          } else {
            setError(t(lang, 'upload_required', { x: f.label }))
          }
          return
        }
        continue
      }
      if (f.required && (values[f.reference] == null || values[f.reference] === "" || (Array.isArray(values[f.reference]) && values[f.reference].length === 0))) {
        setError(t(lang, 'required_field', { x: f.label }))
        return
      }
    }
    // ── Heures (point 4c/4d) ──────────────────────────────────────────────
    // En mode borné (défaut), les selects ne proposent que des créneaux
    setStep("submitting")
    try {
      await submitFormToken(token, values)
      // Point 3 — résa directe : court écran de confirmation puis redirection
      // vers la page parcours. Sinon comportement actuel (écran « Merci »).
      setStep("done")
    } catch (e2) {
      handleSubmitError(e2)
    }
  }

  if (step === "loading") {
    return <Centered><p style={{color:"#888"}}>{t(lang, 'loading')}</p></Centered>
  }
  // B2 — écran expiré dédié (atteint UNIQUEMENT drapeau V2 allumé, cf. fetch).
  if (step === "expired") {
    return <GuestFormExpireV2 lang={lang} onChangeLang={changeLang} />
  }
  if (step === "received") {
    return (
      <GuestFormRecu
        lang={lang}
        onChangeLang={changeLang}
        fields={formConfig ? formConfig.fields : []}
        deja={dejaRecu}
        journeyUrl={journeyUrl}
        onModifier={startModification}
        avisRefus={avisRefus}
      />
    )
  }
  if (step === "error") {
    return (
      <Centered>
        <h1 style={titleStyle}>{t(lang, 'error_title')}</h1>
        <p style={msgStyle}>{error}</p>
        <p style={{...msgStyle, fontSize:11, color:"#aaa", marginTop:14}}>{t(lang, 'error_help')}</p>
      </Centered>
    )
  }
  if (step === "done") {
    // B2 — écran merci V2 : « Nous avons tout reçu » + CTA « Retour à mon
    // séjour » SI journey_url (remplace la redirection auto 1,5 s ; Q-MERCI-OTA
    // étend journey_url au séjour OTA côté form-config). Drapeau éteint : les
    // chemins de redirection actuels ci-dessous restent INTACTS.
    if (formV2) {
      return (
        <GuestFormMerciV2
          lang={lang}
          guestFirstName={guestFirstName}
          journeyUrl={journeyUrl}
          heure={values.time_in || null}
          dateIso={reservation && reservation.start_date ? reservation.start_date : null}
          onChangeLang={changeLang}
          limiteModification={limiteModification}
        />
      )
    }
    // Point 3 — priorité à la page parcours (résa directe) sur le redirect_url
    // legacy. Court écran de confirmation (1,5 s) puis retour à la réservation.
    if (journeyUrl) {
      window.setTimeout(() => { window.location.href = journeyUrl }, 1500)
      return (
        <Centered>
          <h1 style={{...titleStyle, color:DS.green}}>{t(lang, 'thanks_title')}</h1>
          <p style={msgStyle}>{t(lang, 'back_to_booking')}</p>
          <p style={{fontSize:11,color:"#aaa",marginTop:14}}>{t(lang, 'redirecting')}</p>
        </Centered>
      )
    }
    if (formConfig?.redirect_url) {
      window.setTimeout(() => { window.location.href = formConfig.redirect_url }, 2000)
    }
    return (
      <Centered>
        <h1 style={{...titleStyle, color:DS.green}}>{t(lang, 'thanks_title')}</h1>
        <p style={msgStyle}>{t(lang, 'thanks_msg')}</p>
        {formConfig?.redirect_url && <p style={{fontSize:11,color:"#aaa",marginTop:14}}>{t(lang, 'redirecting')}</p>}
      </Centered>
    )
  }

  // B2 — rendu : V2 derrière le drapeau, MÊME CONTRAT DE PROPS + additions.
  if (formV2) {
    return (
      <GuestFormViewV2
        formConfig={formConfig}
        logementName={logementName}
        reservation={reservation}
        schedule={schedule}
        idDocRequired={idDocEffectif}
        billingAddressRequired={billingAddressRequired}
        values={values}
        uploads={uploads}
        lang={lang}
        step={step}
        error={error}
        onSetValue={setValue}
        onChangeLang={changeLang}
        onSubmit={handleSubmit}
        onPickIdFile={startIdUpload}
        guestFirstName={guestFirstName}
        guestDisplayName={guestDisplayName}
        journeyUrl={journeyUrl}
        fieldErrors={fieldErrors}
        onRemoveIdFile={handleRemoveIdFile}
        rgpdOpen={rgpdOpen}
        onToggleRgpd={() => setRgpdOpen(o => !o)}
        limiteModification={limiteModification}
      />
    )
  }

  return (
    <GuestFormView
      formConfig={formConfig}
      logementName={logementName}
      reservation={reservation}
      schedule={schedule}
      idDocRequired={idDocEffectif}
      billingAddressRequired={billingAddressRequired}
      values={values}
      uploads={uploads}
      lang={lang}
      step={step}
      error={error}
      onSetValue={setValue}
      onChangeLang={changeLang}
      onSubmit={handleSubmit}
      onPickIdFile={startIdUpload}
    />
  )
}
